// useSocial.ts — Social media metrics hooks.
// Polling hooks for /api/social/* endpoints.

import { usePolling } from "./useDashboard";

// ── Response types ────────────────────────────────────────────────────────────

export interface SocialMetricRow {
  id: string;
  platform: "meta" | "linkedin" | "x";
  metric_type: "post" | "daily_insight" | "profile" | "tweet";
  date: string;
  data: string; // raw JSON blob — parse on use
  synced_at: string;
}

export interface SocialMetricsResponse {
  metrics: SocialMetricRow[];
  count: number;
  filters: {
    platform: string | null;
    metric_type: string | null;
    since: string;
  };
}

export interface PlatformSummary {
  platform: string;
  followers: number | null;
  post_count: number;
  total_engagement: number;
  latest_synced_at: string | null;
  credential_valid: boolean | null;
}

export interface SocialSummaryResponse {
  summary: {
    meta: PlatformSummary;
    linkedin: PlatformSummary;
    x: PlatformSummary;
  };
  has_data: boolean;
  since: string;
  generated_at: string;
}

// ── Params ────────────────────────────────────────────────────────────────────

export interface SocialMetricsParams {
  platform?: "meta" | "linkedin" | "x";
  metric_type?: "post" | "daily_insight" | "profile" | "tweet";
  since?: string;
  limit?: number;
  offset?: number;
}

// ── useSocialMetrics ──────────────────────────────────────────────────────────

/**
 * Fetches GET /api/social/metrics with optional filters and polls every intervalMs.
 *
 * @param params   Optional query filters (platform, metric_type, since, limit, offset)
 * @param interval Polling interval in ms (default 120 s)
 */
export function useSocialMetrics(
  params?: SocialMetricsParams,
  interval = 120_000,
) {
  const qs = new URLSearchParams();
  if (params?.platform) qs.set("platform", params.platform);
  if (params?.metric_type) qs.set("metric_type", params.metric_type);
  if (params?.since) qs.set("since", params.since);
  if (params?.limit != null) qs.set("limit", String(params.limit));
  if (params?.offset != null) qs.set("offset", String(params.offset));
  const qsStr = qs.toString();
  const url = `/api/social/metrics${qsStr ? `?${qsStr}` : ""}`;

  return usePolling<SocialMetricsResponse>(url, interval);
}

// ── useSocialSummary ──────────────────────────────────────────────────────────

/**
 * Fetches GET /api/social/summary and polls every intervalMs.
 * Returns per-platform follower counts, post counts, and engagement totals.
 *
 * @param interval Polling interval in ms (default 120 s)
 */
export function useSocialSummary(interval = 120_000) {
  return usePolling<SocialSummaryResponse>("/api/social/summary", interval);
}
